import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Card, Modal, Tag, Badge, Table, Radio, Empty } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import UpdateIcon from '@mui/icons-material/Update'
import { Alert } from '@mui/material';
import LoadingComponent from '../../lib/LoadingComponent';
import { getBizID, getServices, getStyle } from '../../libs/todoSlice';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import formatDateToThaiPattern from '../../lib/DateToThaiPattern';
import convertDateTime from '../../lib/ConvertDateTime';
import FadeIn from 'react-fade-in/lib/FadeIn';
import fontColor from '../../lib/fontColor';
import Swal from "sweetalert2";
import dayjs from 'dayjs';

const BACKEND = process.env.REACT_APP_BACKEND_URL
const MONTHS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const ALL_BREAKPOINTS = ["xs", "sm", "md", "lg", "xl", "xxl"]
const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/

// อ่าน list จาก env แบบไม่ทำให้หน้า crash ถ้าค่าเป็น JSON เสีย
const parseEnvList = (value) => {
  try {
    return value ? JSON.parse(value) : []
  } catch {
    return []
  }
}
const listIdCardCheck = parseEnvList(process.env.REACT_APP_HIDDEN_ID_CARD)
const listEmpIdCheck = parseEnvList(process.env.REACT_APP_EMP_ID)

// hover ของการ์ด: ใช้ currentTarget (ตัวการ์ดเอง) ไม่ใช่ target (element ลูกที่เมาส์ชี้อยู่)
const hoverIn = ({ currentTarget }) => {
  currentTarget.style.transform = 'scale(1.1)'
  currentTarget.style.transition = 'all .5s'
}
const hoverOut = ({ currentTarget }) => {
  currentTarget.style.transform = 'scale(1.0)'
}

const customLocale = {
  emptyText: (<Empty description="ไม่มีข้อมูล" />)
};

// CA หมดอายุหรือยัง: ดูจากใบรับรองใบแรก (array) หรือวันที่ yyyy/mm/dd (string)
const isCaExpired = (status) => {
  if (!status) return false
  const now = new Date()
  if (Array.isArray(status) && status.length > 0) {
    const expiredDate = status[0].expiredDate
    return expiredDate ? now > dayjs(expiredDate).toDate() : false
  }
  if (typeof status === 'string' && status.includes('/')) {
    const [year, month, day] = status.split('/').map(Number)
    return now > new Date(year, month - 1, day)
  }
  return false
}

const toMemberRow = (member) => ({
  account_id: member.accountID,
  emp_id: member.emp_id ?? '-',
  name_th: `${member.special_title_th || member.account_title_th || ''} ${member.first_name_th || ''} ${member.last_name_th || ''}`,
  name_eng: `${member.special_title_eng || member.account_title_eng || ''} ${member.first_name_eng || ''} ${member.last_name_eng || ''}`,
  email: member.email ?? '-',
  id_card_num: member.id_card_num,
  tel: member.mobile_no,
  last_login: member.last_login,
  contract_no: Array.isArray(member.contract_no) ? member.contract_no.map(ca => ca.contract_no) : null,
  ca_o: Array.isArray(member.ca) ? member.ca.map(ca => ca.o) : null,
  ca_status: Array.isArray(member.ca) ? member.ca.map(ca => ca.ca_status) : null,
})

// ---------- ส่วนแสดงผลย่อย ----------

const FilterCard = ({ active, accent, iconBackground, icon, count, label, onClick }) => (
  <Card
    style={{
      width: '100%',
      borderRadius: '16px',
      marginBottom: '10px',
      cursor: 'pointer',
      backgroundImage: active ? `linear-gradient( transparent -10%, ${accent} 160%)` : 'none',
    }}
    onMouseEnter={hoverIn}
    onMouseLeave={hoverOut}
    onClick={onClick}
  >
    <Row>
      <Col className="align-self-center" style={{ textAlign: "center" }}>
        {icon({
          style: {
            backgroundColor: iconBackground,
            borderRadius: "50%",
            fontSize: "52px",
            padding: "5px",
            color: iconBackground === accent ? '#fff' : accent,
          },
        })}
      </Col>
      <Col className="align-self-center" style={{ textAlign: "center" }}>
        {count === undefined || count === null ? (
          <LoadingComponent width={100} height={100} />
        ) : (
          <>
            <span style={{ fontSize: "31px", color: accent, fontWeight: "700" }}>{count}</span>
            <br />
            {label}
          </>
        )}
      </Col>
    </Row>
  </Card>
)

const OneIdReadyTag = () => (
  <Tag color="success"><Badge status="success" /> One ID พร้อมใช้งาน</Tag>
)

const CaStatusTags = ({ status, expired }) => {
  if (status == null || (Array.isArray(status) && status.length === 0)) {
    return <OneIdReadyTag />
  }
  let caTag
  if (status === 'waiting_for_admin') {
    caTag = <Tag color="warning"><Badge status="warning" /> รอการอนุมัติจากเจ้าหน้าที่ CA</Tag>
  } else if (status === 'waiting_for_confirm') {
    caTag = <Tag color="warning"><Badge status="warning" /> รอการยืนยันข้อมูล</Tag>
  } else if (expired) {
    caTag = <Tag color="error"><Badge status="error" /> CA หมดอายุ</Tag>
  } else {
    caTag = <Tag color="processing"><Badge status="processing" /> CA พร้อมใช้งาน</Tag>
  }
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <OneIdReadyTag />
      {caTag}
    </div>
  )
}

const certificateBoxStyle = {
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '15px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  position: 'relative',
}
const labelStyle = { color: '#888', fontSize: '0.9rem' }
const headingStyle = { fontWeight: 'bold', color: '#1a3d7c' }

const DnField = ({ label, value, md = 4, className }) => (
  <Col
    xs={12}
    sm={6}
    md={md}
    className={className}
  >
    <div style={labelStyle}>{label}</div>
    <div style={{ wordBreak: 'break-word' }}>
      {value || '-'}
    </div>
  </Col>
)

const CertificateCard = ({ item }) => (
  <div style={certificateBoxStyle}>
    <Row>
      <Col xs={12} sm={6} md={4} className="mb-3">
        <div style={labelStyle}>ชื่อใบรับรอง</div>
        <div
          style={{
            fontWeight: '600',
            wordBreak: 'break-word',
          }}
        >
          {item.name || '-'}
        </div>
      </Col>

      <Col xs={12} sm={6} md={4} className="mb-3">
        <div style={labelStyle}>รหัสใบรับรอง</div>
        <div>{item.credentialId || '-'}</div>
      </Col>

      <Col xs={12} sm={6} md={4} className="mb-3">
        <div style={labelStyle}>วันหมดอายุ</div>
        <div
          style={{
            color: '#ff4d4f',
            fontWeight: 'bold',
          }}
        >
          {dayjs(item.expiredDate).format('DD / MM / YYYY')}
        </div>
      </Col>
    </Row>

    <hr style={{ borderTop: '1px solid #eee' }} />

    <div
      style={{
        ...headingStyle,
        marginBottom: '15px',
      }}
    >
      Certificate DN
    </div>

    <Row>
      <DnField
        label="ตำแหน่ง (t)"
        value={item.certificateDn?.t}
        className="mb-3"
      />

      <DnField
        label="องค์กร (o)"
        value={item.certificateDn?.o}
        className="mb-3"
      />

      <DnField
        label="เลขประจำตัว (oid)"
        value={item.certificateDn?.oid}
        className="mb-3"
      />

      <DnField
        label="หน่วยงาน (ou)"
        value={item.certificateDn?.ou}
        className="mb-3"
      />

      <DnField
        label="ประเทศ (c)"
        value={item.certificateDn?.c}
        className="mb-3"
      />

      <DnField
        label="ชื่อ (cn)"
        value={item.certificateDn?.cn}
        className="mb-3"
      />
    </Row>
  </div>
)

const CertificateList = ({ status }) => {
  if (Array.isArray(status) && status.length > 0) {
    return (
      <div style={{ marginTop: '20px' }}>
        <div style={{ ...headingStyle, marginBottom: '15px' }}>
          ข้อมูลใบรับรอง
        </div>

        <div>
          {status.map((item) => (
            <CertificateCard
              key={item.credentialId}
              item={item}
            />
          ))}
        </div>
      </div>
    )
  }

  if (typeof status === 'string' && status.includes('/')) {
    return (
      <div style={{ marginTop: '20px' }}>
        <div style={{ ...headingStyle, marginBottom: '15px' }}>
          ข้อมูลใบรับรอง
        </div>

        <div style={certificateBoxStyle}>
          <div style={labelStyle}>วันหมดอายุ</div>

          <div
            style={{
              color: '#ff4d4f',
              fontWeight: 'bold',
            }}
          >
            {status.split('/').reverse().join(' / ')}
          </div>
        </div>
      </div>
    )
  }
  return null
}

// แสดง array ของค่าในเซลล์เดียว (หลายสัญญา / หลายหน่วยงาน / หลาย CA)
const renderList = (items, emptyText, renderItem) => {
  if (!Array.isArray(items) || items.length === 0) return <div>{emptyText}</div>
  return items.map((item, index) => <div key={index}>{renderItem(item)}</div>)
}

const renderCaStatus = (status) => {
  if (status === null || status === undefined) return 'ไม่พบข้อมูล'
  if (status === 'waiting_for_admin') return 'รอเจ้าหน้าที่ตรวจสอบ'
  if (status === 'waiting_for_confirm') return 'รอการยืนยันข้อมูล'
  if (status === 'waiting_for_ca_expired') return 'ca หมดอายุ'
  return formatDateToThaiPattern(status)
}

// ---------- หน้าหลัก ----------

const FollowMember = () => {
  const navigate = useNavigate()
  const params = useParams()
  const business_id = useSelector(getBizID)
  const style = useSelector(getStyle)
  const services = useSelector(getServices)

  // รายการสมาชิก
  const [filter, setFilter] = useState('all')   // 'all' | 'noCA' | 'hasCA'
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchText, setSearchText] = useState('')   // ข้อความในช่องพิมพ์
  const [keyword, setKeyword] = useState('')         // ข้อความที่ "ยืนยันค้นหาแล้ว" (ใช้ยิง API)
  const [reloadTick, setReloadTick] = useState(0)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const reload = () => setReloadTick((tick) => tick + 1)

  // modal ติดตามสถานะ CA
  const [modalFollowOpen, setModalFollowOpen] = useState(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [checkStatus, setCheckStatus] = useState(null)

  // modal ส่ง Email ซ้ำ
  const [resendOpen, setResendOpen] = useState(false)
  const [resendTarget, setResendTarget] = useState(null)
  const [emailList, setEmailList] = useState([])
  const [emailForResend, setEmailForResend] = useState('')
  const [changeEmail, setChangeEmail] = useState('not-change')   // 'change' | 'not-change'
  const [resendMessage, setResendMessage] = useState({})

  // ที่เดียวที่ยิง API รายการ: เปลี่ยนหน้า / ขนาดหน้า / การ์ด / คำค้นที่ยืนยันแล้ว / reload
  // abort request เก่าเมื่อมี request ใหม่ กันผลลัพธ์ตอบกลับมาสลับลำดับ
  useEffect(() => {
    if (!business_id) return
    const controller = new AbortController()
    setLoading(true)
    axios.get(`${BACKEND}/api/v6/businesses/employees/follow`, {
      params: {
        biz_id: business_id,
        page,
        perPage: pageSize,
        only_one_id: filter === 'noCA',
        only_has_ca: filter === 'hasCA',
        ...(keyword && { searching: keyword }),
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        setData(data.data)
        setLoading(false)
      })
      .catch((error) => {
        // ถูกยกเลิกเพราะมี request ใหม่แทน: request ใหม่จะปิด loading เอง
        if (axios.isCancel(error)) return
        const status = error.response?.status
        if (status === 401) {
          navigate(`/${params.prefix}`)
        } else if (status === 403) {
          navigate(`/forbidden`)
        }
        setLoading(false)
      })
    return () => controller.abort()
  }, [business_id, page, pageSize, filter, keyword, reloadTick])

  const members = useMemo(() => (data?.list ?? []).map(toMemberRow), [data])
  const total = { all: data?.total, noCA: data?.no_ca, hasCA: data?.has_ca }[filter]

  const doSearch = () => {
    setPage(1)
    setKeyword(searchText.trim())
  }

  const selectFilter = (value) => {
    setFilter(value)
    setPage(1)
  }

  // ---------- อัปเดตข้อมูล CA ----------
  const updateCaData = async () => {
    Swal.fire({
      title: "กำลังดำเนินการอัปเดต CA...",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    })
    try {
      await axios.get(`${BACKEND}/api/v7/businesses/ca/fetch`, { params: { biz_id: business_id } })
      Swal.fire({ icon: 'success', title: 'ดำเนินการอัปเดตสำเร็จ' }).then(reload)
    } catch (error) {
      const message = error.response?.data?.message
      Swal.fire({
        icon: 'error',
        title: 'ดำเนินการอัปเดตไม่สำเร็จ',
        text: typeof message === 'string' ? message : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
      })
    }
  }

  // ---------- ติดตามสถานะ CA ----------
  const openFollowModal = async (record) => {
    setCheckStatus(null)
    setModalFollowOpen(true)
    setLoadingModal(true)
    try {
      const { data } = await axios.get(`${BACKEND}/api/v7/businesses/user/ca/detail`, {
        params: { account_id: record.account_id, biz_id: business_id },
      })
      setCheckStatus(data.data.ca_status ?? null)
    } catch (error) {
      console.error('openFollowModal/err :>> ', error)
    } finally {
      setLoadingModal(false)
    }
  }

  const closeFollowModal = () => {
    setModalFollowOpen(false)
    // ดึงสถานะ CA ล่าสุดสำเร็จแล้ว -> รีเฟรชรายการให้ตรงกัน
    if (checkStatus !== null) reload()
  }

  // ---------- ส่ง Email ซ้ำ ----------
  const openResendDialog = async (record) => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/v5/businesses/ra/email-tel`, {
        params: { account_id: record.account_id, business_id },
      })
      setEmailForResend('')
      setChangeEmail('not-change')
      setResendMessage({})
      setEmailList(data.data.email)
      setResendTarget(record)
      setResendOpen(true)
    } catch (error) {
      console.error('openResendDialog/err :>> ', error)
      Swal.fire({ icon: 'error', title: 'ไม่สามารถดึงรายการอีเมลได้' })
    }
  }

  const sendResendMail = async () => {
    const subject = services.includes('import-excel-rama') ? 'rama' : 'pmuc'
    try {
      await axios.post(`${BACKEND}/api/v5/businesses/ra/resend-mail`, {
        account_id: resendTarget.account_id,
        business_id: business_id,
        email: emailForResend,
        change: changeEmail === 'change',
        subject,
      })
      setResendMessage({ message: 'ส่ง Email สำเร็จ', status: 'success' })
      setTimeout(() => {
        setEmailForResend('')
        setEmailList([])
        setResendOpen(false)
        setResendTarget(null)
        setResendMessage({})
      }, 2000)
    } catch (error) {
      const message = error.response?.data?.message
      if (message === 'This Email dupicate.') {   // สะกดตาม response ของ backend
        setResendMessage({ message: 'Email มีอยู่ในระบบแล้ว', status: 'warning' })
      } else if (message === 'This account_id has not been username.') {
        setResendMessage({ message: 'บัญชีนี้ไม่พบ Username,กรุณาตั้งค่า Username', status: 'warning' })
      } else {
        setResendMessage({ message: 'ส่ง Email ไม่สำเร็จ', status: 'error' })
      }
      setTimeout(() => setResendMessage({}), 2000)
    }
  }

  const columns = [
    {
      title: 'ลำดับ',
      width: 70,
      dataIndex: 'index',
      key: 'index',
      fixed: 'left',
      responsive: ALL_BREAKPOINTS,
      render: (_, __, index) => ((page * pageSize) - pageSize) + index + 1,
    },
    {
      title: 'รหัสพนักงาน',
      dataIndex: 'emp_id',
      width: 150,
      fixed: 'left',
      responsive: ALL_BREAKPOINTS,
      hidden: !listEmpIdCheck.includes(business_id),
    },
    {
      title: (<div>ชื่อ - นามสกุล <br />(ภาษาไทย)</div>),
      dataIndex: 'name_th',
      key: 'name_th',
      width: 200,
      responsive: ALL_BREAKPOINTS,
    },
    {
      title: (<div>ชื่อ - นามสกุล <br />(ภาษาอังกฤษ)</div>),
      dataIndex: 'name_eng',
      key: 'name_eng',
      width: 200,
      responsive: ALL_BREAKPOINTS,
    },
    {
      title: 'หมายเลขบัตรประชาชน',
      width: 170,
      dataIndex: 'id_card_num',
      key: 'id_card_num',
      responsive: ALL_BREAKPOINTS,
      hidden: listIdCardCheck.includes(business_id),
      render: (_) => (_ === 'DUMMY' ? 'ไม่มีข้อมูล' : _),
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'tel',
      key: 'tel',
      width: 150,
      responsive: ALL_BREAKPOINTS,
    },
    {
      title: 'อีเมล',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      responsive: ALL_BREAKPOINTS,
    },
    {
      title: 'เลขที่สัญญา',
      dataIndex: 'contract_no',
      key: 'contract_no',
      width: 150,
      render: (list) => renderList(list, '-', (item) => item ?? '-'),
    },
    {
      title: 'หน่วยงาน',
      dataIndex: 'ca_o',
      key: 'ca_o',
      width: 210,
      render: (list) => renderList(list, 'ไม่พบข้อมูล', (item) => item ?? 'ไม่พบข้อมูล'),
    },
    {
      title: 'วันหมดอายุ CA',
      dataIndex: 'ca_status',
      key: 'ca_status',
      width: 210,
      render: (list) => renderList(list, 'ไม่พบข้อมูล', renderCaStatus),
    },
    {
      title: 'เข้าสู่ระบบล่าสุด',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 210,
      render: (last_login) => {
        if (last_login === '-') return last_login
        return last_login ? convertDateTime(last_login) : null
      },
    },
    {
      title: 'ส่ง Email ซ้ำ',
      key: 'resend',
      width: 150,
      render: (_, record) => {
        // เคยเข้าสู่ระบบแล้ว -> ไม่ต้องส่งซ้ำ
        if (record.last_login !== null) return '-'
        return (
          <Button
            onClick={() => openResendDialog(record)}
            style={{ backgroundColor: '#F3F5FA', color: '#3776E9' }}>
            Resend-Email
          </Button>
        )
      },
    },
    {
      title: 'จัดการ',
      key: 'operation',
      width: 130,
      responsive: ALL_BREAKPOINTS,
      render: (_, record) => (
        <Button
          onClick={() => openFollowModal(record)}
          style={{ backgroundColor: '#F3F5FA', color: '#3776E9' }}>
          ติดตามสถานะ
        </Button>
      ),
    },
  ].filter(item => !item.hidden);

  const now = new Date()

  // โหลดครั้งแรก (ยังไม่มีข้อมูลเลย) ใช้ spinner ทั้งหน้า ครั้งต่อไปให้ตารางหมุนเอง
  // เพื่อไม่ให้ช่องค้นหา/การ์ดถูก unmount ทุกครั้งที่เปลี่ยนหน้า
  if (!data && loading) {
    return <LoadingComponent />
  }

  return (
    <>
      <Row>
        <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
          <FilterCard
            active={filter === 'all'}
            accent="#3776E9"
            iconBackground="#3776E9"
            icon={(props) => <PeopleOutlinedIcon {...props} />}
            count={data?.total}
            label="สมาชิกทั้งหมด"
            onClick={() => selectFilter('all')}
          />
        </Col>
        <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
          <FilterCard
            active={filter === 'noCA'}
            accent="#53BE35"
            iconBackground="#F3F5FA"
            icon={(props) => <PersonOutlinedIcon {...props} />}
            count={data?.no_ca}
            label="ยังไม่มี CA"
            onClick={() => selectFilter('noCA')}
          />
        </Col>
        <Col xxl={3} xl={4} lg={4} md={12} sm={12} xs={12}>
          <FilterCard
            active={filter === 'hasCA'}
            accent="#F2BE47"
            iconBackground="#F3F5FA"
            icon={(props) => <PersonOutlinedIcon {...props} />}
            count={data?.has_ca}
            label="มี CA แล้ว"
            onClick={() => selectFilter('hasCA')}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: "20px", marginTop: "20px" }}>
        <Col xl={12} lg={12} md={6} sm={12} xs={12}>
          <h5>ค้นหา</h5>
        </Col>
        <Col className="d-flex justify-content-start" xs={12} sm={6}>
          <Input.Group compact>
            <Input
              value={searchText}
              size="large"
              style={{ width: "calc(100% - 40px)" }}
              placeholder="ค้นหาด้วยชื่อ-นามสกุล หรือ เลขบัตรประจำตัวประชาชน"
              onChange={({ target }) => setSearchText(target.value)}
              onPressEnter={doSearch}
              maxLength={100}
            />
            <Button
              icon={<SearchOutlined />}
              size="large"
              style={{ borderRadius: "0px 4px 4px 0px" }}
              onClick={doSearch}
            />
          </Input.Group>
        </Col>

        <Col className="d-flex justify-content-end" lg={6} xl={6} sm={12} xs={12}>
          <Button
            style={{
              width: 177,
              fontSize: '14px',
              backgroundColor: style.secondary_color ? style.secondary_color : undefined,
              color: style.secondary_color ? fontColor(style.secondary_color) : undefined,
            }}
            onClick={updateCaData}
          >
            อัปเดตข้อมูล CA
          </Button>
        </Col>
      </Row>
      <Table
        locale={customLocale}
        columns={columns}
        dataSource={members}
        rowKey="account_id"
        loading={loading}
        scroll={{ x: 1400, y: 400 }}
        pagination={{
          current: page,
          pageSize: pageSize,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          total: total,
        }}
      />

      <Modal
        title={
          <span>
            ติดตามสถานะ (ล่าสุด {now.getDate()} {MONTHS_TH[now.getMonth()]} {now.getFullYear() + 543})
          </span>
        }
        centered
        open={modalFollowOpen}
        onCancel={closeFollowModal}
        footer={false}
        width={Array.isArray(checkStatus) && checkStatus.length > 0 ? 1000 : 500}
        style={{
          top: 20,
          maxWidth: '95vw',
        }}
      >
        <Card style={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}>
          {loadingModal ? (
            <LoadingComponent />
          ) : (
            <>
              <Row>
                <Col xs={12} md={6} className="d-flex flex-column">
                  <div style={{ ...headingStyle, marginBottom: '10px' }}>
                    สถานะ
                  </div>

                  <div style={headingStyle}>
                    การสร้าง CA
                  </div>
                </Col>

                <Col xs={12} md={6} className="d-flex flex-column">
                  <div style={headingStyle}>
                    สถานะล่าสุด
                  </div>

                  <span style={{ marginBottom: "10px", marginTop: "10px" }}>
                    <CaStatusTags
                      status={checkStatus}
                      expired={isCaExpired(checkStatus)}
                    />
                  </span>
                </Col>
              </Row>

              <div
                style={{
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: '8px',
                }}
              >
                <CertificateList status={checkStatus} />
              </div>
            </>
          )}
        </Card>
      </Modal>

      <Modal
        title={<span>ส่ง Email ซ้ำ</span>}
        centered
        open={resendOpen}
        onCancel={() => setResendOpen(false)}
        footer={false}
      >
        <div className="row">
          <div className="col my-3">
            <Radio.Group
              onChange={(e) => {
                setChangeEmail(e.target.value)
                setEmailForResend("")
              }}
              value={changeEmail}
            >
              <Radio value="change">เปลี่ยนอีเมล</Radio>
              <Radio value="not-change">ไม่เปลี่ยนอีเมล</Radio>
            </Radio.Group>
          </div>
        </div>
        <div className="row">
          {changeEmail === "change" ? (
            <div className="col my-3">
              <Input
                size="large"
                placeholder="Email"
                prefix={<EmailOutlinedIcon />}
                onChange={(e) => setEmailForResend(e.target.value)}
                value={emailForResend}
              />
            </div>
          ) : (
            <div className="col">
              <label className="mb-2">กรุณาเลือกเมลทีต้องการส่งซ้ำ</label>
              <div className="row">
                <Radio.Group
                  value={emailForResend}
                  onChange={(e) => setEmailForResend(e.target.value)}
                >
                  {emailList.map((email) => (
                    <div className="row" key={email}>
                      <div className="col">
                        <Radio value={email}>{email}</Radio>
                      </div>
                    </div>
                  ))}
                </Radio.Group>
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col my-3">
            {resendMessage.message && (
              <FadeIn>
                <Alert severity={resendMessage.status}>
                  {resendMessage.message}
                </Alert>
              </FadeIn>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col my-3">
            <Button
              onClick={() => setResendOpen(false)}
              style={{ backgroundColor: "#F3F5FA" }}
            >
              ยกเลิก
            </Button>
            <Button
              disabled={!EMAIL_PATTERN.test(emailForResend)}
              onClick={sendResendMail}
              style={{ backgroundColor: "#F3F5FA", float: "right", color: "#3776E9" }}
            >
              ส่ง
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
export default FollowMember;